import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit } from "@angular/core";
import { BpeService, EmailTemplate } from "../../bpe/bpe.service";
import { EventService } from "../../../services/event.service";
import { BpeUtil } from "../../bpe/bpe.util";
import { S25Util } from "../../../util/s25-util";
import { ExecutionEnvironment } from "../../../services/execution.enviroment";
import { S25LoadingApi } from "../../s25-loading/loading.api";
import { Bind } from "../../../decorators/bind.decorator";
import { ModalComponent } from "../modal.component";

@Component({
    template: `
        @if (isInit) {
            <s25-modal-header [data]="data"></s25-modal-header>
            <div class="modal-body">
                <s25-loading-inline [model]="{}"></s25-loading-inline>
                @switch (status) {
                    @case ("noReference") {
                        <div>Please provide an event reference</div>
                    }
                    @case ("noTemplate") {
                        <div>No emails are configured. Please select or add a template to this scenario</div>
                    }
                    @case ("noEvent") {
                        <div>Failed to load event, please make sure your event reference is correct</div>
                    }
                    @case ("noSent") {
                        <div>No emails would have been sent</div>
                    }
                    @case ("sent") {
                        <p class="c-margin-bottom--half">
                            The following email{{ data.templates.length === 1 ? "" : "s" }} would have been sent
                        </p>
                        @for (template of data.templates; track template; let index = $index) {
                            <s25-simple-collapse [expanded]="index < 2" [header]="'Template: ' + template.name">
                                <div class="email">
                                    <s25-ng-email-template-preview
                                        [name]="template.name"
                                        [code]="template.code"
                                        [scenarioName]="data.name"
                                        [reference]="data.reference"
                                    ></s25-ng-email-template-preview>
                                </div>
                            </s25-simple-collapse>
                        }
                    }
                    @case ("failed") {
                        <div>Something went wrong loading preview</div>
                    }
                }
            </div>
        }
    `,
    styles: `
        .email {
            font-style: italic;
            padding: 1em;
            border-top: 1px solid black;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ModalEmailScenarioPreviewComponent
    extends ModalComponent<{
        templates: {
            name: EmailTemplate["itemName"];
            code: EmailTemplate["code"];
        }[];
        name: string;
        code: string;
        reference: string;
    }>
    implements OnInit
{
    private readonly elementRef = inject(ElementRef);

    isInit = false;
    status: "noReference" | "noTemplate" | "loading" | "noEvent" | "noSent" | "sent" | "failed" = "loading";

    async ngOnInit() {
        if (!this.data.reference) this.status = "noReference";
        else if (!this.data.templates.length) this.status = "noTemplate";
        else await this.checkWillSend();
        this.isInit = true;
        this.cd.detectChanges();
    }

    async checkWillSend() {
        this.isLoading(true);
        const id = await EventService.getEventIdByLocator(this.data.reference).catch(this.failed);
        if (!id) return this.failed();
        const event = await BpeService.getEventData(id, { cache: true });
        const vars = await BpeUtil.getVariables(event, null, event);
        const code = BpeUtil.interpolateString(vars, this.data.code);
        this.isLoading(false);

        try {
            const willSend = ExecutionEnvironment.run(code, {
                vars,
                eventData: event,
                util: BpeUtil.util,
            });
            this.status = willSend ? "sent" : "noSent";
        } catch (error: any) {
            S25Util.showError(error);
            this.status = "failed";
        }
    }

    isLoading(loading: boolean) {
        if (loading) S25LoadingApi.init(this.elementRef.nativeElement);
        else S25LoadingApi.destroy(this.elementRef.nativeElement);
    }

    @Bind
    failed() {
        this.status = "noEvent";
        this.isLoading(false);
        this.cd.detectChanges();
    }
}
